import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { FinanceDefault } from "src/app/settings-module/models";

@Component({
  selector: "app-finance-default-form",
  templateUrl: "./finance-default-form.component.html",
  styleUrls: ["./finance-default-form.component.scss"]
})
export class FinanceDefaultFormComponent implements OnInit {
  @Input() financeDefault: FinanceDefault;
  @Input() waitOnAction = false;
  @Output() submitFinanceDefault = new EventEmitter<FinanceDefault>();
  @Output() unsavedChanges = new EventEmitter();

  financeDefaultForm: UntypedFormGroup = this.formBuilder.group({
    newTermsShown: new UntypedFormArray([]),
    usedTermsShown: new UntypedFormArray([]),
    certTermsShown: new UntypedFormArray([]),
    customTermDefault: [0, [Validators.required, Validators.min(0)]],
    daysToFirstPay: [0, [Validators.required, Validators.min(0)]],
    dmvFees: [0, [Validators.required, Validators.min(0)]],
    gapAmortFactor: [0, [Validators.required, Validators.min(0)]],
    gapDepreciation: [0, [Validators.required, Validators.min(0)]],
    taxRate: [0, [Validators.required, Validators.min(0)]]
  });

  constructor(private formBuilder: UntypedFormBuilder) { }

  ngOnInit() {
    this.initializeFinanceDefaultForm();
    this.subToFormChanges();
  }

  // INITIALIZATION

  private initializeFinanceDefaultForm() {
    this.financeDefaultForm.patchValue({
      customTermDefault: this.financeDefault.customTermDefault,
      daysToFirstPay: this.financeDefault.daysToFirstPay,
      dmvFees: this.financeDefault.dmvFees,
      gapAmortFactor: this.financeDefault.gapAmortFactor,
      gapDepreciation: this.financeDefault.gapDepreciation,
      taxRate: this.financeDefault.taxRate
    });

    const initialNewTermsShown = this.financeDefault.newTermsShown || [];
    const initialUsedTermsShown = this.financeDefault.usedTermsShown || [];
    const initialCertTermsShown = this.financeDefault.certTermsShown || [];

    initialNewTermsShown.forEach(term => {
      this.addTermControl("newTermsShown", term);
    });

    initialUsedTermsShown.forEach(term => {
      this.addTermControl("usedTermsShown", term);
    });

    initialCertTermsShown.forEach(term => {
      this.addTermControl("certTermsShown", term);
    });
  }

  private subToFormChanges() {
    this.financeDefaultForm.valueChanges.subscribe(() => {
      this.unsavedChanges.emit();
    });
  }

  // ACTIONS

  onSubmitFinanceDefaultForm() {
    const financeDefault: FinanceDefault = this.financeDefaultForm.value;
    this.submitFinanceDefault.emit(financeDefault);
    this.financeDefaultForm.markAsPristine();
  }

  // UI CONTROL & RENDERING

  onAddTerm(termType: string) {
    this.addTermControl(termType);
    this.financeDefaultForm.markAsDirty();
  }

  onRemoveTerm(termType: string, termIndex: number) {
    const formArray = this.financeDefaultForm.get(termType) as UntypedFormArray;
    formArray.removeAt(termIndex);
    this.financeDefaultForm.markAsDirty();
  }

  get newTermsShown(): AbstractControl[] {
    const formArray = this.financeDefaultForm.get("newTermsShown") as UntypedFormArray;
    return formArray.controls;
  }

  get usedTermsShown(): AbstractControl[] {
    const formArray = this.financeDefaultForm.get("usedTermsShown") as UntypedFormArray;
    return formArray.controls;
  }

  get certTermsShown(): AbstractControl[] {
    const formArray = this.financeDefaultForm.get("certTermsShown") as UntypedFormArray;
    return formArray.controls;
  }

  valid(controlName: string): boolean {
    const control = this.financeDefaultForm.get(controlName);
    return control.valid;
  }

  value(controlName: string): number {
    return this.financeDefaultForm.value[ controlName ];
  }

  // FORM VALIDATION AND HELPERS

  private addTermControl(termType: string, term?: number) {
    const formArray = this.financeDefaultForm.get(termType) as UntypedFormArray;
    const control = new UntypedFormControl(term || null, [Validators.required, Validators.min(1)]);
    formArray.push(control);
  }

  touchedInvalid(controlName: string): boolean {
    const control = this.financeDefaultForm.get(controlName);
    return control.touched && control.invalid;
  }

  touchedInvalidTerm(termType: string, termIndex: number): boolean {
    const control = this.financeDefaultForm.get([termType, termIndex]);
    return control.touched && control.invalid;
  }

}
